import { useEffect, useState } from 'react';
import { Box, IconButton, Paper, styled, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import {
  GridCellParams,
  GridColDef,
  GridSortModel,
} from '@mui/x-data-grid-pro';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

import { ApplicationGlobalSettingResponse } from '../../../data/models/application-setting.model';

import {
  deleteApplicationGlobalSettingAsync,
  fetchAllApplicationGlobalSettingsAsync,
  selectGlobalSettings,
  selectLoadedGlobalSettings,
  selectLoadingGlobalSettings,
} from '../../../data/settings.slice';
import {
  fetchLookupItemsAsync,
  selectApplicationSettings,
  selectApplicationTitles,
} from '../../../data/lookup.slice';

import { DataGridPro } from '../../tables/DataGridPro';
import ConfirmationDialog from '../../ConfirmationDialog';
import PageHeader from '../../controls/PageHeader';
import { DataTableContainer } from './GroupDataTable';
import CommonToolbarWithSearchBar from './CommonToolbarWithSearchBar';
import ApplicationGlobalSettingDialog from './ApplicationGlobalSettingDialog';

const PREFIX = 'ApplicationGlobalSettings';

const classes = {
  container: `${PREFIX}-container`,
  tableContainer: `${PREFIX}-tableContainer`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(1),
  },
  [`& .${classes.tableContainer}`]: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'stretch',
    minWidth: 0,
  },
}));

export default function ApplicationGlobalSettings() {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const isSuperReaderUser = useIsSuperReaderUser();

  const isReadOnly = isSuperReaderUser;

  const [searchText, setSearchText] = useState('');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'applicationSettingName', sort: 'asc' },
  ]);
  const [settingToEdit, setSettingToEdit] =
    useState<ApplicationGlobalSettingResponse | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteMessage, setConfirmDeleteMessage] = useState('');
  const [confirmAddOpen, setConfirmAddOpen] = useState(false);
  const [settingToDelete, setSettingToDelete] =
    useState<ApplicationGlobalSettingResponse>();

  const globalSettings = useAppSelector(selectGlobalSettings);
  const loadingGlobalSettings = useAppSelector(selectLoadingGlobalSettings);
  const loadedGlobalSettings = useAppSelector(selectLoadedGlobalSettings);
  const applicationTitles = useAppSelector(selectApplicationTitles);
  const applicationSettings = useAppSelector(selectApplicationSettings);

  const columns: GridColDef[] = [
    {
      field: 'applicationSetting',
      headerName: t('applicationGlobalSettings.applicationSettingHeader'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.ApplicationSetting.applicationSettingName}</>;
      },
    },
    {
      field: 'applicationTitle',
      headerName: t('applicationGlobalSettings.applicationTitleHeader'),
      width: 200,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.ApplicationTitle.applicationName}</>;
      },
    },
    {
      field: 'applicationSettingValue',
      headerName: t('applicationGlobalSettings.applicationSettingValueHeader'),
      width: 200,
      renderCell: (params: GridCellParams) => {
        const value = params.row.applicationSettingValue;
        const label = value.substring(0, 1).toUpperCase() + value.substring(1);
        return <>{label}</>;
      },
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 100,
      renderCell: (params: GridCellParams) => {
        return (
          <>
            {!isReadOnly && (
              <>
                <IconButton onClick={() => handleEditGlobalSetting(params.row)}>
                  <Tooltip title={t('common.edit')}>
                    <EditIcon color="primary" />
                  </Tooltip>
                </IconButton>
                <IconButton
                  onClick={() => handleDeleteGlobalSetting(params.row)}>
                  <Tooltip title={t('common.delete')}>
                    <DeleteForeverIcon color="error" />
                  </Tooltip>
                </IconButton>
              </>
            )}
          </>
        );
      },
    },
  ];

  const onSearchTextChange = (text: string) => {
    setSearchText(text);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const confirmAddGlobalSetting = () => {
    setSettingToEdit(null);
    setConfirmAddOpen(false);
    setEditOpen(true);
  };

  const handleCloseEditSettingDialog = () => {
    setEditOpen(false);
  };

  const cancelAddGlobalSetting = () => {
    setConfirmAddOpen(false);
  };

  const handleAddGlobalSetting = () => {
    setSettingToEdit(null);
    setConfirmAddOpen(true);
  };

  const handleEditGlobalSetting = (
    globalSetting: ApplicationGlobalSettingResponse,
  ) => {
    setSettingToEdit(globalSetting);
    setEditOpen(true);
  };

  const handleDeleteGlobalSetting = (
    globalSetting: ApplicationGlobalSettingResponse,
  ) => {
    const msg = t('applicationGlobalSettings.confirmDeleteMessage', {
      name: globalSetting.ApplicationSetting.applicationSettingName,
      bc: globalSetting.ApplicationSetting.applicationSettingBC,
    });
    setConfirmDeleteMessage(msg);
    setSettingToDelete(globalSetting);
    setConfirmDeleteOpen(true);
  };

  const confirmDeleteGlobalSetting = () => {
    if (settingToDelete) {
      dispatch(
        deleteApplicationGlobalSettingAsync(
          settingToDelete.applicationGlobalSettingID,
        ),
      );
    }
    setConfirmDeleteOpen(false);
  };

  const cancelDeleteGlobalSetting = () => {
    setConfirmDeleteOpen(false);
    setConfirmDeleteMessage('');
  };

  useEffect(() => {
    dispatch(fetchLookupItemsAsync('applicationTitle'));
    dispatch(fetchLookupItemsAsync('applicationSetting'));
  }, []);

  useEffect(() => {
    const sort = sortModel[0]
      ? {
          column: sortModel[0].field,
          direction: sortModel[0].sort as 'asc' | 'desc',
        }
      : null;
    dispatch(fetchAllApplicationGlobalSettingsAsync(searchText, sort));
  }, [dispatch, searchText, sortModel]);

  return (
    <StyledPaper>
      <Box className={classes.container}>
        <PageHeader
          title={t('applicationGlobalSettings.title')}
          returnLink="/settings"
          returnLinkText={t('settings.title')}
        />
      </Box>
      <CommonToolbarWithSearchBar
        label={t('applicationGlobalSettings.search')}
        placeholder={t('applicationGlobalSettings.searchPlaceholder')}
        button={isReadOnly ? null : t('applicationGlobalSettings.add')}
        onCreate={handleAddGlobalSetting}
        onSearchTextChange={onSearchTextChange}
        searchText={searchText}
        disabled={loadingGlobalSettings}
      />
      <DataTableContainer className={classes.tableContainer}>
        <DataGridPro
          rows={globalSettings}
          getRowId={(row) => row.applicationGlobalSettingID}
          columns={columns}
          loading={loadingGlobalSettings}
          rowSelection={false}
          disableRowSelectionOnClick={true}
          disableColumnMenu={true}
          disableColumnSelector={true}
          hideFooter={true}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          sortingOrder={['asc', 'desc']}
          initialState={{
            sorting: {
              sortModel: [{ field: 'applicationSettingName', sort: 'asc' }],
            },
          }}
        />
      </DataTableContainer>
      {loadedGlobalSettings && globalSettings.length === 0 && (
        <div>{t('applicationGlobalSettings.noGlobalSettings')}</div>
      )}
      <ApplicationGlobalSettingDialog
        open={editOpen}
        globalSetting={settingToEdit}
        isReadOnly={isReadOnly}
        applicationTitles={applicationTitles}
        applicationSettings={applicationSettings}
        onClose={() => handleCloseEditSettingDialog()}
      />
      <ConfirmationDialog
        title={t('applicationGlobalSettings.confirmAddTitle')}
        message={t('applicationGlobalSettings.confirmAddMessage')}
        open={confirmAddOpen}
        positiveAction={confirmAddGlobalSetting}
        negativeAction={cancelAddGlobalSetting}
      />
      <ConfirmationDialog
        title={t('applicationGlobalSettings.confirmDeleteTitle')}
        message={confirmDeleteMessage}
        open={confirmDeleteOpen}
        positiveAction={confirmDeleteGlobalSetting}
        negativeAction={cancelDeleteGlobalSetting}
      />
    </StyledPaper>
  );
}
